import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { lastValueFrom, map } from 'rxjs';
import { environment } from '@env';

@Injectable({
  providedIn: 'root',
})
export class LoginApiService {
  private http = inject(HttpClient);
  private baseUrl = environment.baseUrl;

  private clientId = environment.loginApiService.clientId;
  private baseUrlOauth2 = environment.loginApiService.baseUrlOauth2;
  private redirectUrl = environment.loginApiService.redirectUrl;

  private state = new Date().getUTCSeconds().toString();

  public getAuthToken(
    code: string,
    codeVerifier: string
  ): Promise<{ access_token: string }> {
    const body = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('client_id', this.clientId)
      .set('redirect_uri', this.redirectUrl)
      .set('code', code)
      .set('code_verifier', codeVerifier)
      .set('scope', 'api');

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return lastValueFrom(
      this.http.post<{ access_token: string }>(
        `${this.baseUrlOauth2}/token`,
        body.toString(),
        { headers }
      )
    );
  }

  public async getAuthApiCall(codeChallenge: string): Promise<string> {
    try {
      const params = {
        response_type: 'code',
        client_id: this.clientId,
        redirect_uri: this.redirectUrl,
        scope: 'api',
        state: this.state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
      };

      const queryString = new URLSearchParams(params).toString();

      const url = `${this.baseUrlOauth2}/authorize?${queryString}`;

      const resp = await fetch(url, {
        method: 'GET',
        credentials: 'include',
      });
      let testStateMatch = resp.url?.match(/(?<=\bstate=)[^&]+/);

      let codeMatch = resp.url?.match(/(?<=\bcode=)[^&]+/);
      if (!codeMatch || (testStateMatch && testStateMatch[0] !== this.state)) {
        return '';
      }
      return codeMatch[0];
    } catch (error) {
      throw error;
    }
  }

  public getCSRFToken() {
    return lastValueFrom(
      this.http
        .get<{ token: string }>(`${this.baseUrl}/csrf-token`)
        .pipe(
          map(csrfResp => {
            return csrfResp.token;
          })
        )
    );
  }

  public getLoginFinish(body: object, csrfToken: string): Promise<object> {
    return lastValueFrom(
      this.http.post(
        `${this.baseUrl}/webauthn/login/finish`,
        body,
        {
          headers: {
            'X-XSRF-TOKEN': csrfToken,
          },
        }
      )
    );
  }

  public getLoginOptions(csrfToken: string) {
    return lastValueFrom(
      this.http.post(
        `${this.baseUrl}/webauthn/login/options`,
        {},
        {
          headers: {
            'X-XSRF-TOKEN': csrfToken,
          },
        }
      )
    );
  }
}
